import { DatePicker, Unifree, YesObject } from '@yes.technology/react-toolkit'
import { useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { MODIFIABLE_FIELDS_DENYLIST } from 'shared/constants'
import useRelationSelectionType from 'site/Renderer/components/ObjectView/hooks/useRelationSelectionType'
import { FieldValuesValue } from '../../../FilterFields/types'
import useTableContext from '../../hooks/useTableContext'
import RelationSelectionField from './RelationSelectionField'
import { EditableCellInputProps } from './TableCell.types'

export default function EditableCellInput({
  value,
  formattedValue,
  columnName,
  objectUuidOrIndex
}: EditableCellInputProps) {
  const {
    readOnlyColumns,
    setChangedFieldValues,
    changedFieldValues,
    objectclass,
    fields,
    relationFields
  } = useTableContext()
  const disabled =
    MODIFIABLE_FIELDS_DENYLIST.includes(columnName) ||
    readOnlyColumns.includes(columnName)
  const { language } = useTranslation()
  const field = fields?.[columnName]
  const label = field?.display_text.des
  const type = field?.type || 'STRING'
  const inputRef = useRef<HTMLInputElement>(null)

  const fallbackObjectclassUuid =
    objectclass?.relation_classes?.[columnName]?.uuid_objectclass || ''

  const { objectclassUuid, isUniselection } = useRelationSelectionType({
    relationFields,
    columnName,
    fallbackObjectclassUuid
  })

  const currentValue = (() => {
    if (
      changedFieldValues[objectUuidOrIndex] &&
      columnName in changedFieldValues[objectUuidOrIndex]
    ) {
      return changedFieldValues[objectUuidOrIndex][columnName]
    }

    if (type === 'RELATION') {
      return value
    }

    return formattedValue
  })()

  const onChange = (inputValue: FieldValuesValue) => {
    const normalizedValue = Array.isArray(inputValue)
      ? inputValue.map(({ des, uuid }) => ({ des, uuid }))
      : inputValue

    if (objectUuidOrIndex) {
      setChangedFieldValues({
        ...changedFieldValues,
        [objectUuidOrIndex]: {
          ...changedFieldValues[objectUuidOrIndex],
          [columnName]: normalizedValue
        }
      })
      return
    }
  }

  if (type === 'RELATION') {
    const selectedOptions = Array.isArray(currentValue)
      ? (currentValue as YesObject[])
      : []

    return (
      <RelationSelectionField
        objectclassUuid={objectclassUuid}
        isUniselection={isUniselection}
        selectedOptions={selectedOptions}
        onChangeValues={onChange}
        disabled={disabled}
        inputRef={inputRef}
        label={label}
        renderInPortal
      />
    )
  }

  if (type === 'DATETIME') {
    return (
      <DatePicker
        {...{ disabled, onChange }}
        heightClass='regular'
        format='date-time'
        lang={language}
        value={typeof currentValue === 'string' ? currentValue : ''}
        aria-label={label}
      />
    )
  }

  return (
    <Unifree
      {...{ disabled, onChange }}
      value={(currentValue || '').toString()}
      aria-label={label}
    />
  )
}
